import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import Paper from "@mui/material/Paper";

import TableContainer from "@mui/material/TableContainer";
import MuiTable from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { AccessorKeyColumnDefBase, ColumnDefBase, IdIdentifier, StringHeaderIdentifier } from "@tanstack/react-table";

import Box from "ui/atom/Box";
import Icon, { type IconName } from "ui/atom/Icon";


type TableProps<D> = {
	data: D[] | undefined;
	columns: ((AccessorKeyColumnDefBase<D, string> & Partial<IdIdentifier<D, string>>)
	| (AccessorKeyColumnDefBase<D, number> & Partial<IdIdentifier<D, number>>)
	| (ColumnDefBase<D, unknown> & StringHeaderIdentifier)
	| (ColumnDefBase<D, unknown> & IdIdentifier<D, unknown>))[];
	icon: IconName;
	height?: number;
	loading?: boolean;
};

type LoadingProps<D> = {
	columns?: TableProps<D>["columns"];
	height?: number;
	rows?: number;
};

const Empty = ({ icon }: { icon: IconName }) => {
	const style = {
		root: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 3,
			p: 6
		},
		icon: {
			alignItems: "center",
			justifyContent: "center",
			p: 2,
			borderRadius: "50%",
			backgroundColor: "action.hover"
		},
		box: {
			textAlign: "center",
			maxWidth: 400
		}
	} as SxProps;

	return (
		<Card sx={style.root}>
			<Box sx={style.icon}>
				<Icon name={icon} size={32} />
			</Box>

			<Box.Column gap={1} sx={style.box}>
				<Typography variant="h6" color="text.secondary" fontWeight={500}>
					No result found
				</Typography>
				<Typography variant="body2" color="text.disabled" sx={{ lineHeight: 1.6 }}>
					There is no data to display at this time. Records will appear here when available.
				</Typography>
			</Box.Column>
		</Card>
	);
};

Table.Loading = <D,>({ columns, height, rows = 4 }: LoadingProps<D>) => (
	<TableBody>
		{Array.from({ length: rows }).map((_, i) => (
			<TableRow key={`skeleton-row-${i}`}>
				{columns?.map((c, j) => (
					<TableCell sx={{ width: c.size, minWidth: c.minSize, maxWidth: c.maxSize, height }} key={`skeleton-cell-${i}-${j}`}>
						<Skeleton variant="text" width="100%" />
					</TableCell>
				))}
			</TableRow>
		))}
	</TableBody>
);

export default function Table<D>({ data, columns, loading, height, icon }: TableProps<D>) {
	const theme = useTheme();
	const table = useReactTable({ data: data || [], columns, getCoreRowModel: getCoreRowModel() });

	const style = {
		header: {
			backgroundColor: "secondary.main",
			color: "common.white"
		},
		table: {
			"td, th": {
				p: {
					xs: theme.spacing(0.75, 1),
					sm: theme.spacing(0.75, 2)
				}
			}
		}
	} as SxProps;

	if (!loading && (!data || data.length === 0)) return <Empty icon={icon} />;
	return (
		<TableContainer component={Paper}>
			<MuiTable stickyHeader size="small" sx={style.table}>
				<TableHead sx={style.header}>
					{table.getHeaderGroups().map(headerGroup => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map(header => (
								<TableCell sx={{ ...style.header, width: header.column.columnDef.size, minWidth: header.column.columnDef.minSize, maxWidth: header.column.columnDef.maxSize }} key={header.id}>
									{header.isPlaceholder
										? null
										: flexRender(header.column.columnDef.header, header.getContext())
									}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableHead>
				{loading
					? <Table.Loading columns={columns} height={height} />
					: <TableBody>
						{table.getRowModel().rows.map((row, i) => (
							<TableRow hover key={i}>
								{row.getVisibleCells().map(cell => (
									<TableCell sx={{ width: cell.column.columnDef.size, minWidth: cell.column.columnDef.minSize, maxWidth: cell.column.columnDef.maxSize }} key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				}
			</MuiTable>
		</TableContainer>
	);
}